import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const DARK = {
  mode: 'dark',
  bg: '#080a0f', card: '#0f1218', el: '#161b24', hov: '#1c2230',
  brd: '#1e2536', brdL: '#2a3348',
  // WCAG AA contrast ratios (4.5:1 minimum for normal text):
  // text #e4e8f0 on #080a0f ≈ 17:1 ✓ | muted #95a5c0 on #0f1218 ≈ 6.4:1 ✓ | dim #7b8ba3 on #0f1218 ≈ 4.7:1 ✓
  text: '#e4e8f0', muted: '#95a5c0', dim: '#7b8ba3',
  // pri darkened so white-on-pri passes 4.5:1 (was #5b6cf0 @ 4.33:1).
  // priL brightened for use as text-on-dark-bg (≥4.5:1 on #0f1218).
  pri: '#4a58e5', priL: '#97a6ff', priD: 'rgba(74,88,229,.15)', priS: 'rgba(74,88,229,.28)',
  ok: '#10b981', okD: 'rgba(16,185,129,.12)',
  warn: '#f59e0b', warnD: 'rgba(245,158,11,.12)',
  err: '#ef4444', errD: 'rgba(239,68,68,.12)',
  cyan: '#06b6d4', cyanD: 'rgba(6,182,212,.12)',
  // mag brightened so mag-on-magD overlay passes 4.5:1
  // (was #c026d3 @ 3.28:1, #d946ef @ 3.91:1, now #e879f9 @ ~6.6:1).
  mag: '#e879f9', magD: 'rgba(232,121,249,.14)',
};

const LIGHT = {
  mode: 'light',
  bg: '#f0f2f5', card: '#ffffff', el: '#f5f6f8', hov: '#ebedf2',
  brd: '#e0e3ea', brdL: '#d0d4dd',
  // WCAG AA contrast ratios (4.5:1 minimum for normal text):
  // text #1a1d26 on #ffffff ≈ 15:1 ✓ | muted #5a6577 on #ffffff ≈ 5.6:1 ✓ | dim #707a8c on #ffffff ≈ 4.6:1 ✓
  text: '#1a1d26', muted: '#5a6577', dim: '#707a8c',
  pri: '#4f5bd5', priL: '#4f5bd5', priD: 'rgba(79,91,213,.08)', priS: 'rgba(79,91,213,.18)',
  ok: '#059669', okD: 'rgba(5,150,105,.08)',
  warn: '#d97706', warnD: 'rgba(217,119,6,.08)',
  err: '#dc2626', errD: 'rgba(220,38,38,.08)',
  cyan: '#0891b2', cyanD: 'rgba(8,145,178,.08)',
  mag: '#a21caf', magD: 'rgba(162,28,175,.08)',
};

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('phoenix_theme') || 'dark'; }
    catch { return 'dark'; }
  });

  const theme = mode === 'dark' ? DARK : LIGHT;

  const toggleTheme = useCallback(() => {
    setMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('phoenix_theme', next); } catch {}
      return next;
    });
  }, []);

  const setTheme = useCallback((m) => {
    setMode(m);
    try { localStorage.setItem('phoenix_theme', m); } catch {}
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, mode, toggleTheme, setTheme, isDark: mode === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Re-export theme objects for places that need static access (rare)
export { DARK, LIGHT };
